import React, { useState, useEffect } from 'react';
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  notification,
  Result,
  Row,
  Select,
  Skeleton,
  Statistic,
  Switch,
  Table,
  Tag,
  theme,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined, DownOutlined, EditOutlined, ExportOutlined } from "@ant-design/icons";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";
import {
  apiGetManagedAccount,
  apiGetManagedAccountMetrics,
  apiGetManagedAccountVirtualAccounts,
  apiUpdateManagedAccount,
  apiExportManagedAccountReport,
} from "../../../../api/virtualAccounts";
import { formatDateTime, formatPrice } from "../../../../utils/format";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import "./ManagedAccountDetails.scss";

const { Content } = Layout;
const { Search } = Input;

const STATUS_LABELS = {
  PLANNED: "Planejado",
  PAID: "Pago",
  WAITING_CONFIRMATION: "Aguardando Confirmação",
  REVERSED: "Estornado",
  CANCELED: "Cancelado",
  ERROR: "Rejeitado",
  UNKNOWN: "Desconhecido",
};

const STATUS_COLORS = {
  PLANNED: "#1890ff",
  PAID: "#21ae41",
  WAITING_CONFIRMATION: "#faad14",
  REVERSED: "#722ed1",
  CANCELED: "#8c8c8c",
  ERROR: "#da0419",
  UNKNOWN: "#595959",
};

const NOTIFICATION_ACTION_LABELS = {
  BANK_SLIP: "Boleto",
  TRANSFER: "Transferência",
};

const ManagedAccountDetails = () => {
  const { id } = useParams();
  const [accountDetails, setAccountDetails] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [isDetailsFetching, setIsDetailsFetching] = useState(false);
  const [isMetricsFetching, setIsMetricsFetching] = useState(false);
  const [isVAFetching, setIsVAFetching] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, total: 0 });
  const [searchText, setSearchText] = useState("");
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportStatuses, setExportStatuses] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const isMobile = useIsMobileHook(1600);
  const [form] = Form.useForm();

  const fetchAccountDetails = async () => {
    setIsDetailsFetching(true);
    try {
      const data = await apiGetManagedAccount(id);
      setAccountDetails(data);
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os detalhes da conta gerenciada.",
        placement: "topRight",
      });
    } finally {
      setIsDetailsFetching(false);
    }
  };

  const fetchMetrics = async () => {
    setIsMetricsFetching(true);
    try {
      const data = await apiGetManagedAccountMetrics(id);
      setMetrics(data);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as métricas.",
        placement: "topRight",
      });
    } finally {
      setIsMetricsFetching(false);
    }
  };

  const fetchVirtualAccounts = async (params) => {
    setIsVAFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiGetManagedAccountVirtualAccounts(id, filteredParams);
      setVirtualAccounts(json.results || []);
      setPagination((prev) => ({
        ...prev,
        total: json.count,
        current: params.page || 1,
      }));
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as contas virtuais.",
        placement: "topRight",
      });
    } finally {
      setIsVAFetching(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchAccountDetails();
      fetchMetrics();
      fetchVirtualAccounts({ page: 1 });
    }
  }, [id]);

  const onSearch = (value) => {
    setSearchText(value);
    fetchVirtualAccounts({
      page: 1,
      search: value || undefined,
    });
  };

  const onChangePage = (paginationParams) => {
    fetchVirtualAccounts({
      page: paginationParams.current,
      search: searchText || undefined,
    });
  };

  const openEditModal = () => {
    form.setFieldsValue({
      name: accountDetails?.name,
      automation_enabled: accountDetails?.automation_enabled,
      notify_actions_to: accountDetails?.notify_actions_to || [],
      notify_on_actions: accountDetails?.notify_on_actions || [],
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsUpdating(true);
      await apiUpdateManagedAccount(id, values);
      notification.success({
        message: "Sucesso",
        description: "Conta gerenciada atualizada com sucesso.",
        placement: "topRight",
      });
      setEditModalOpen(false);
      form.resetFields();
      fetchAccountDetails();
    } catch (error) {
      if (error.errorFields) return;
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao atualizar a conta gerenciada.",
        placement: "topRight",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    form.resetFields();
  };

  const handleExportSubmit = async () => {
    if (exportStatuses.length === 0) {
      notification.warning({
        message: "Atenção",
        description: "Selecione pelo menos um status para exportar.",
        placement: "topRight",
      });
      return;
    }
    setIsExporting(true);
    try {
      const { blob } = await apiExportManagedAccountReport(id, exportStatuses);
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
      const accountName = accountDetails?.name || id;
      const filename = `btg_export_${accountName}_${timestamp}.xlsm`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportModalOpen(false);
      setExportStatuses([]);
      notification.success({
        message: "Sucesso",
        description: "Relatório exportado com sucesso.",
        placement: "topRight",
      });
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao exportar o relatório.",
        placement: "topRight",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCancel = () => {
    setExportModalOpen(false);
    setExportStatuses([]);
  };

  const exportStatusOptions = [
    { label: "Planejado", value: "PLANNED" },
    { label: "Pago", value: "PAID" },
    { label: "Aguardando Confirmação", value: "WAITING_CONFIRMATION" },
    { label: "Estornado", value: "REVERSED" },
    { label: "Cancelado", value: "CANCELED" },
    { label: "Rejeitado", value: "ERROR" },
    { label: "Desconhecido", value: "UNKNOWN" },
  ];

  const handleRowClick = (record) => {
    const rowId = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(rowId);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(rowId);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const buildChartData = (dataObj, labelMap) => {
    if (!dataObj) return [];
    return Object.entries(dataObj)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: labelMap[key] || key,
        value,
        statusKey: key,
      }));
  };

  const renderPieLabel = ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`;

  const renderPieChart = (data, colorMap) => {
    if (!data || data.length === 0) {
      return <Empty description="Sem dados" />;
    }
    return (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label={renderPieLabel}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colorMap[entry.statusKey]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const accountTypeBadgeMap = {
    SUB_SELLER_PF: { label: "Pessoa Física", className: "badge badge-info" },
    SUB_SELLER_PJ: { label: "Pessoa Jurídica", className: "badge badge-success" },
    SELLER_PF: { label: "Pessoa Física", className: "badge badge-info" },
    SELLER_PJ: { label: "Pessoa Jurídica", className: "badge badge-success" },
    PF: { label: "Pessoa Física", className: "badge badge-info" },
    PJ: { label: "Pessoa Jurídica", className: "badge badge-success" },
  };

  const renderAccountType = (type) => {
    const config = accountTypeBadgeMap[type];
    if (!config) return <span className="badge black">Indefinido</span>;
    return <span className={config.className}>{config.label}</span>;
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: ["account", "display_name"],
      key: "display_name",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Tipo",
      dataIndex: ["account", "type"],
      key: "type",
      render: renderAccountType,
      responsive: ["sm"],
    },
    {
      title: "Saldo",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <span dangerouslySetInnerHTML={{ __html: formatPrice(balance / 100) }} />
      ),
      responsive: ["md"],
    },
    {
      title: "Data de Criação",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) => (
        <p
          style={{ textTransform: "capitalize" }}
          dangerouslySetInnerHTML={{ __html: formatDateTime(created_at) }}
        />
      ),
      responsive: ["sm"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
        Nome:{" "}
        <p>{record.account?.display_name}</p>
      </span>
      <span>
        Tipo:{" "}
        {renderAccountType(record.account?.type)}
      </span>
      <span>
        Saldo:{" "}
        <p dangerouslySetInnerHTML={{ __html: formatPrice(record.balance / 100) }} />
      </span>
      <span>
        Data de Criação:{" "}
        <p dangerouslySetInnerHTML={{ __html: formatDateTime(record.created_at) }} />
      </span>
    </div>
  );

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/contas-virtuais")} type="primary">
              Voltar para Contas Virtuais
            </Button>
          }
        />
      );
    }
  };

  const transferChartData = buildChartData(metrics?.transfers, STATUS_LABELS);
  const bankSlipChartData = buildChartData(metrics?.bank_slips, STATUS_LABELS);

  return (
    <div className="ManagedAccountDetails">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">{accountDetails?.name || "Conta Gerenciada"}</h2>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/contas-virtuais")}
            >
              Voltar
            </Button>
          </div>
          <Divider />
          <Breadcrumb.Item>Contas Gerenciadas</Breadcrumb.Item>
          <Breadcrumb.Item>Detalhes</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {isDetailsFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <>
              <Card
                className="settings-card"
                title="Configurações"
                extra={
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={openEditModal}
                  />
                }
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={6}>
                    <strong>Tipo:</strong>{" "}
                    <span className={`badge ${accountDetails?.type === 'CORA' ? 'badge-cora' : 'badge-btg'}`}>{accountDetails?.type}</span>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <strong>Automação:</strong>{" "}
                    {accountDetails?.automation_enabled ? (
                      <Tag color="green">Ativada</Tag>
                    ) : (
                      <Tag color="red">Desativada</Tag>
                    )}
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <strong>E-mails de notificação:</strong>
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {accountDetails?.notify_actions_to?.length > 0 ? (
                        accountDetails.notify_actions_to.map((email) => (
                          <Tag key={email} style={{ width: 'fit-content' }}>{email}</Tag>
                        ))
                      ) : (
                        <Tag style={{ width: 'fit-content' }}>Nenhum</Tag>
                      )}
                    </div>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <strong>Notificar nas ações:</strong>{" "}
                    {accountDetails?.notify_on_actions?.length > 0 ? (
                      accountDetails.notify_on_actions.map((action) => (
                        <Tag key={action}>{NOTIFICATION_ACTION_LABELS[action] || action}</Tag>
                      ))
                    ) : (
                      <Tag>Nenhuma</Tag>
                    )}
                  </Col>
                </Row>
              </Card>

              {isMetricsFetching ? (
                <Skeleton active />
              ) : (
                <Row gutter={[24, 24]} className="metrics-row">
                  <Col xs={24} lg={8}>
                    <Card>
                      <div className="metric-stat">
                        <Statistic
                          title="Saldo Total"
                          value={metrics?.balance ? metrics.balance / 100 : 0}
                          prefix="R$"
                          precision={2}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </div>
                      <div className="metric-stat">
                        <Statistic
                          title="Transferências Planejadas"
                          value={metrics?.transfers?.PLANNED || 0}
                        />
                      </div>
                      <div className="metric-stat">
                        <Statistic
                          title="Boletos Pendentes"
                          value={metrics?.bank_slips?.PLANNED || 0}
                        />
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Card className="chart-card" title="Status das Transferências">
                      {renderPieChart(transferChartData, STATUS_COLORS)}
                    </Card>
                  </Col>
                  <Col xs={24} lg={8}>
                    <Card className="chart-card" title="Status dos Boletos">
                      {renderPieChart(bankSlipChartData, STATUS_COLORS)}
                    </Card>
                  </Col>
                </Row>
              )}

              <Row className="actions-row" style={{ marginTop: 16, marginBottom: 16 }}>
                <Col>
                  <h3 className="subtitle-page" style={{ marginBottom: 12 }}>Ações</h3>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "btg",
                          label: "Planilha BTG",
                          onClick: () => setExportModalOpen(true),
                        },
                      ],
                    }}
                  >
                    <Button icon={<ExportOutlined />}>
                      Exportar <DownOutlined />
                    </Button>
                  </Dropdown>
                </Col>
              </Row>

              <Divider />
              <h3 className="subtitle-page" style={{ marginBottom: '24px' }}>Contas Virtuais</h3>

              <div style={{ marginBottom: 16 }}>
                <Search
                  placeholder="Buscar por nome da conta"
                  allowClear
                  enterButton="Buscar"
                  onSearch={onSearch}
                  style={{ width: 300 }}
                />
              </div>

              {isVAFetching ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={columns}
                  locale={locale}
                  expandable={{
                    expandedRowRender: isMobile ? expandedRowRender : undefined,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (_expanded, record) => handleRowClick(record),
                    responsive: ["xl"],
                  }}
                  dataSource={
                    virtualAccounts &&
                    virtualAccounts.map((item) => ({ ...item, key: item.id }))
                  }
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    showSizeChanger: false,
                  }}
                  bordered
                  onChange={onChangePage}
                  onRow={(record) => ({
                    onClick: () => navigate(`/conta-gerenciada/${id}/conta-virtual/${record.id}`),
                    style: { cursor: "pointer" },
                  })}
                />
              )}
            </>
          )}
        </Content>
      </Layout>

      <Modal
        title="Editar Conta Gerenciada"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={handleEditCancel}
        confirmLoading={isUpdating}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nome">
            <Input />
          </Form.Item>
          <Form.Item name="automation_enabled" label="Automação" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notify_actions_to" label="Notificar ações para">
            <Select mode="tags" placeholder="Digite os e-mails" />
          </Form.Item>
          <Form.Item name="notify_on_actions" label="Notificar nas ações">
            <Select
              mode="multiple"
              placeholder="Selecione as ações"
              options={[
                { label: "Boleto", value: "BANK_SLIP" },
                { label: "Transferência", value: "TRANSFER" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Exportar Planilha BTG"
        open={exportModalOpen}
        onOk={handleExportSubmit}
        onCancel={handleExportCancel}
        confirmLoading={isExporting}
        okText="Exportar"
        cancelText="Cancelar"
      >
        <p style={{ marginBottom: 12 }}>Selecione os status a exportar:</p>
        <Checkbox.Group
          options={exportStatusOptions}
          value={exportStatuses}
          onChange={setExportStatuses}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />
      </Modal>
    </div>
  );
};

export default ManagedAccountDetails;
